import {
  FieldLabel,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Crncy } from '@paywithextend/localization'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LocaleDropdown from 'src/components/LocaleDropdown/LocaleDropdown'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TestId from 'src/components/TestId/TestId'
import { useFormFields } from 'src/hooks'
import { getLocaleInfo, LocaleType } from 'src/localization/locales'
import Trns from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  PATCH_USER_LOCALE_INIT,
  PatchUserActions,
} from 'src/redux/actions/data/users.actions'
import {
  selectIsCspAuthenticatedFromState,
  selectNullSafeCurrencyFromLocale,
} from 'src/redux/selectors/auth.selectors'
import { PatchUserRequest } from 'src/requests/patchUser.request'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface LocaleInfoBoxProps {
  className?: string
  user: UserListModel
  patchingUser: boolean
  patchingUserLocale: boolean
  errorDetailMap?: ErrorDetailMap<string>
  patchUser: (
    body: Partial<PatchUserRequest>,
    patchType: PatchUserActions,
  ) => void
}

export type LocaleInfoFlatFields = {
  locale: string
}

const LocaleInfoBox: React.FC<LocaleInfoBoxProps> = (
  props,
): React.ReactElement => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [exampleFormat, setExampleFormat] = useState<React.ReactElement>()
  const currency = useSelector(selectNullSafeCurrencyFromLocale)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)

  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<LocaleInfoFlatFields>( // TODO: should make this string | undefined to be totally transparent
    {
      locale: props.user.locale,
    },
    () => ({}),
    props.errorDetailMap,
  )
  useEffect((): void => {
    setFlatFields({
      locale: props.user.locale,
    })
  }, [props.user])

  const onSuccessfulSubmit = (flatFields: LocaleInfoFlatFields): void => {
    props.patchUser(
      {
        locale: flatFields.locale,
      },
      {
        type: PATCH_USER_LOCALE_INIT,
      },
    )
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()

    if (flatFieldsPassedValidation) {
      onSuccessfulSubmit(flatFields)
    }
  }

  useEffect((): void => {
    if (props.patchingUserLocale === false) {
      setIsEditing(false)
    }
  }, [props.patchingUserLocale])

  useEffect((): void => {
    moment.locale(flatFields['locale'])
    setExampleFormat(
      <span
        lang={flatFields['locale']}
        css={css`
          margin-top: 16px;
          white-space: pre-wrap;
          display: flex;
          flex-direction: column;
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        <span className={'time'}>
          {moment.tz(Date.now(), props.user.timezone).format('LT')}
        </span>
        <span className={'date'}>
          {moment.tz(Date.now(), props.user.timezone).format('dddd, LL')}
        </span>
        <span className={'shorthand-date'}>
          {moment.tz(Date.now(), props.user.timezone).format('L')}
        </span>
        <Crncy
          locale={flatFields['locale']}
          defaultDisplayCurrency={currency}
          currency={currency}
          amount={1234.56}
        />
      </span>,
    )
  }, [flatFields['locale'], props.user.currency])

  const localeInfo: LocaleType = getLocaleInfo(flatFields['locale'])

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Language and Region'} />
        {!isEditing && (
          <TestId testId={'localeUpdateButton'}>
            <HoverableLink
              onClick={(): void => {
                setIsEditing(true)
              }}
              ariaLabelSource={'Update'}
              css={css`
                display: flex;
                align-items: center;
              `}
              colorSvgOnHover
              disabled={isCspAuthenticated}
            >
              <Trns source={'Update'} />
              <SvgUpdate
                className={'svg-update'}
                css={css`
                  transform: scale(0.5);
                `}
              />
            </HoverableLink>
          </TestId>
        )}
      </span>
      <form
        onSubmit={onSubmit}
        css={css`
          margin-top: 30px;
          display: flex;
          flex-direction: column;
          .field-label {
            overflow: visible;
          }
        `}
      >
        {!isEditing && (
          <span
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <FieldLabel className={'locale-label'}>
              <Trns source={'Current Language and Region'} />
            </FieldLabel>
            <div
              css={css`
                display: flex;
                height: '100%';
                align-items: left;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns source={localeInfo.displayName} />
            </div>
          </span>
        )}
        {isEditing && (
          <TestId testId={'localeDropdown'}>
            <span>
              <LocaleDropdown
                className={'locale-input'}
                selectedValue={flatFields.locale}
                chooseOption={(val: string): void =>
                  onFlatFieldChange('locale', val)
                }
                errors={flatFieldsErrors.locale}
              />
            </span>
          </TestId>
        )}
        <span
          css={css`
            margin-top: 40px;
            display: flex;
            flex-direction: column;
            border-top: 1px solid ${styleConstants.colors.gray_medium};
          `}
        >
          <span
            css={css`
              margin-top: 40px;
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns source={'Region Format Example'} />
          </span>
          {exampleFormat}
        </span>
        {isEditing && (
          <span
            css={css`
              margin-top: 40px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <Trns
                source={
                  'Applying this setting will refresh your ${appName} session'
                }
                expressions={{
                  appName: <AppName />,
                }}
              />
            </span>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 40px;
                margin-top: 16px;
              `}
            >
              <TestId testId={'localeConfirmButton'}>
                <Button
                  design={ButtonDesigns.designCta}
                  type={ButtonTypes.submit}
                  text={
                    <Trns
                      source={'Change to ${localeDisplayName}'}
                      expressions={{
                        localeDisplayName: (
                          <Trns source={localeInfo.displayName} />
                        ),
                      }}
                    />
                  }
                  loading={props.patchingUserLocale}
                  disabled={props.patchingUser}
                  big
                />
              </TestId>
              <HoverableLink
                onClick={(): void => {
                  setIsEditing(false)
                }}
                underline
                ariaLabelSource={'Cancel'}
              >
                <Trns source={'Cancel'} />
              </HoverableLink>
            </div>
          </span>
        )}
      </form>
    </Box>
  )
}

export default LocaleInfoBox
